import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { SECTION_ROW_LABEL_WIDTH, SECTION_ROW_HEADING, SECTION_ROW_HEADINGS } from './constants';

/**
 * Two-column settings row: a fixed-width label column (title, description and an optional
 * `meta` slot) beside a fluid content column. Rows stack on mobile. Separator lines between
 * sibling rows come from the `divider` flag so a list of rows reads as one grouped form.
 */
const SectionRow = ({
    title,
    description,
    meta = null,
    children,
    divider = true,
    labelWidth = SECTION_ROW_LABEL_WIDTH,
    heading = SECTION_ROW_HEADING.H3,
    className,
    labelClassName,
    contentClassName,
    ...rest
}) => {
    const Heading = SECTION_ROW_HEADINGS.includes(heading) ? heading : SECTION_ROW_HEADING.H3;
    const labelStyle = labelWidth === SECTION_ROW_LABEL_WIDTH ? undefined : { flexBasis: labelWidth, maxWidth: labelWidth };

    return (
        <div className={classNames('ds-section-row', { 'ds-section-row--divider': divider }, className)} {...rest}>
            <div className={classNames('ds-section-row__label', labelClassName)} style={labelStyle}>
                {title != null && <Heading className="ds-section-row__title">{title}</Heading>}
                {description != null && description !== '' && (
                    <p className="ds-section-row__description">{description}</p>
                )}
                {meta && <div className="ds-section-row__meta">{meta}</div>}
            </div>
            <div className={classNames('ds-section-row__content', contentClassName)}>{children}</div>
        </div>
    );
};

SectionRow.propTypes = {
    /** Row title, rendered as the `heading` element */
    title: PropTypes.node,
    /** Secondary text under the title */
    description: PropTypes.node,
    /** Extra label-column content below the description (hint, badge, checkbox) */
    meta: PropTypes.node,
    /** Content column */
    children: PropTypes.node,
    /** Draw the separator line under the row (off for the last row of a group) */
    divider: PropTypes.bool,
    /** Label column width in px on tablet and up */
    labelWidth: PropTypes.number,
    /** Element used for the title */
    heading: PropTypes.oneOf(SECTION_ROW_HEADINGS),
    /** Extra class on the row */
    className: PropTypes.string,
    /** Extra class on the label column */
    labelClassName: PropTypes.string,
    /** Extra class on the content column */
    contentClassName: PropTypes.string,
};

export { SECTION_ROW_LABEL_WIDTH, SECTION_ROW_HEADING, SECTION_ROW_HEADINGS };
export default SectionRow;
